/**
 * Change Queue Manager
 * Manages file change events and schedules incremental updates
 */

import type { IContentIndexService } from './ContentIndexService'

export interface FileChange {
  path: string
  type: 'created' | 'modified' | 'deleted'
  timestamp: number
}

export class ChangeQueueManager {
  private queue: Map<string, FileChange> = new Map()
  private processTimer: NodeJS.Timeout | null = null
  private isProcessing = false
  private readonly BATCH_DELAY = 1000 // Wait 1 second to batch changes
  private readonly MAX_BATCH_SIZE = 50
  
  constructor(
    private contentIndexService: IContentIndexService,
    private onProgressUpdate?: (progress: any) => void
  ) {}
  
  /**
   * Add a file change to the queue
   */
  addChange(change: FileChange): void {
    // Use path as key to deduplicate changes to the same file
    this.queue.set(change.path, change)
    
    // Schedule processing
    this.scheduleProcessing()
  }
  
  /**
   * Add multiple changes at once
   */
  addChanges(changes: FileChange[]): void {
    for (const change of changes) {
      this.queue.set(change.path, change)
    }
    
    this.scheduleProcessing()
  }
  
  /**
   * Schedule batch processing of changes
   */
  private scheduleProcessing(): void {
    // If already processing, don't schedule
    if (this.isProcessing) return
    
    // Clear existing timer
    if (this.processTimer) {
      clearTimeout(this.processTimer)
    }
    
    // Schedule new processing
    this.processTimer = setTimeout(() => {
      this.processChanges()
    }, this.BATCH_DELAY)
  }
  
  /**
   * Process queued changes
   */
  private async processChanges(): Promise<void> {
    if (this.isProcessing || this.queue.size === 0) return
    
    this.isProcessing = true
    this.processTimer = null
    
    try {
      // Get batch of changes to process
      const batch = Array.from(this.queue.entries())
        .slice(0, this.MAX_BATCH_SIZE)
        .map(([path, change]) => change)
      
      // Clear processed items from queue
      for (const change of batch) {
        this.queue.delete(change.path)
      }
      
      // Group changes by type for efficient processing
      const created: string[] = []
      const modified: string[] = []
      const deleted: string[] = []
      
      for (const change of batch) {
        switch (change.type) {
          case 'created':
            created.push(change.path)
            break
          case 'modified':
            modified.push(change.path)
            break
          case 'deleted':
            deleted.push(change.path)
            break
        }
      }
      
      // Process deletions first
      if (deleted.length > 0) {
        await this.processDeleted(deleted)
      }
      
      // Process creations and modifications together
      const toUpdate = [...created, ...modified]
      if (toUpdate.length > 0) {
        await this.processUpdated(toUpdate)
      }
      
    } catch (error) {
      console.error('Error processing change queue:', error)
    } finally {
      this.isProcessing = false
      
      // If there are more items in queue, schedule another batch
      if (this.queue.size > 0) {
        this.scheduleProcessing()
      }
    }
  }
  
  /**
   * Process deleted files
   */
  private async processDeleted(paths: string[]): Promise<void> {
    for (const path of paths) {
      try {
        await this.contentIndexService.removeDocument(path)
      } catch (error) {
        console.error(`Failed to remove document ${path}:`, error)
      }
    }
  }
  
  /**
   * Process created/updated files
   */
  private async processUpdated(paths: string[]): Promise<void> {
    let processed = 0
    
    for (const path of paths) {
      try {
        // Notify progress
        if (this.onProgressUpdate) {
          this.onProgressUpdate({
            mode: 'incremental',
            current: processed,
            total: paths.length,
            currentFile: path,
            startTime: Date.now()
          })
        }
        
        // Check if it's a markdown file
        if (!path.endsWith('.md')) {
          processed++
          continue
        }
        
        // Get file content
        const content = await this.readFileContent(path)
        if (!content) {
          processed++
          continue
        }
        
        // Update index
        await this.contentIndexService.indexDocument(path, content)
        
        processed++
      } catch (error) {
        console.error(`Failed to update document ${path}:`, error)
        processed++
      }
    }
    
    // Clear progress
    if (this.onProgressUpdate) {
      this.onProgressUpdate(null)
    }
  }
  
  /**
   * Read file content (platform-specific)
   */
  private async readFileContent(path: string): Promise<string | null> {
    try {
      // Check if we're in Electron
      if (typeof window !== 'undefined' && (window as any).api?.readFile) {
        const result = await (window as any).api.readFile(path)
        if (!result || !result.success) {
          return null
        }

        // Maintain backwards compatibility with different response shapes
        if (typeof result.content === 'string') {
          return result.content
        }

        if (typeof result.data === 'string') {
          return result.data
        }

        return null
      }
      
      // Try to read from vaultStore for browser
      const { vaultStore } = await import('$lib/stores/vaultStore')
      const { get } = await import('svelte/store')
      const vault = get(vaultStore)
      
      if (vault.vaultHandle) {
        const content = await vaultStore.readFile(path)
        return content
      }
      
      return null
    } catch (error) {
      console.error(`Failed to read file ${path}:`, error)
      return null
    }
  }
  
  /**
   * Get current queue size
   */
  getQueueSize(): number {
    return this.queue.size
  }
  
  /**
   * Check if processing
   */
  isProcessingChanges(): boolean {
    return this.isProcessing
  }
  
  /**
   * Clear the queue
   */
  clear(): void {
    if (this.processTimer) {
      clearTimeout(this.processTimer)
      this.processTimer = null
    }
    this.queue.clear()
    this.isProcessing = false
  }
}
